import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MoreHorizontal, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { RowIdentity } from '@/components/activity/work-row-visuals';
import { launchSessionHref, type TodayLaunch } from '@/components/activity/today-launches';
import type { AppObjectNavigationState } from '@/components/apps/AppRecordsView';
import { usePopover } from '@/components/apps/usePopover';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import { type DueRitual, loadRitualHistory, saveRitualOccurrence, undoRitualOccurrence } from '@/lib/workspace-rituals';
import { describeRhythm, type RitualOccurrence } from '@shared/types/workspace-rituals';
import { appRoute, COMPANY_ROUTE, ritualTarget, TEAM_ROUTE } from './ritual-targets';

const ROW_CLASS = 'rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 sm:rounded-none sm:border-0';
const COMMAND_CLASS = `inline-flex min-h-8 items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 ${DENSE_ACTION_CLASS}`;
const MENU_CLASS = 'absolute right-0 top-9 z-20 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]';
const ITEM_CLASS = 'flex w-full items-center px-3 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted';

const formatDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const dayName = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long' });

/** What the global chat receives to help hold the ritual. */
export const ritualChatRequest = (entry: DueRitual): string => [
  `Je fais mon rituel « ${entry.ritual.title} » (${describeRhythm(entry.ritual)}).`,
  entry.ritual.detail ?? '',
  'Aide-moi à le mener : ce qu’il faut regarder, puis les questions à me poser pour décider.',
].filter(Boolean).join(' ');

interface Props {
  entry: DueRitual;
  today: string;
  onChange: (occurrence: DueRitual['occurrence']) => void;
  /** An app ritual: the objects of its app still to handle, taken out of the list. */
  launch?: TodayLaunch | null;
}

/**
 * One ritual in Mon travail, a row like any task (21/10/2026, « il ouvre son
 * travail »): « Faire » opens what is ready — the séance of its app, the team's
 * week, the review of the apps, its page, or else the chat — and « Terminer »
 * keeps this time with one optional line of what was decided. It comes back on
 * its next day; the earlier times stay readable from its menu.
 */
export const RitualCard = ({ entry, today, onChange, launch = null }: Props) => {
  const navigate = useNavigate();
  const assistant = useOptionalAppAssistant();
  const { ritual, occursOn, occurrence } = entry;
  const finishMenu = usePopover();
  const moreMenu = usePopover();
  const [decision, setDecision] = useState(occurrence?.decision ?? '');
  const [history, setHistory] = useState<RitualOccurrence[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = ritualTarget(ritual.route);
  const count = launch?.recordIds.length ?? 0;

  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await action(); } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le rituel n’a pas été enregistré.');
    } finally { setBusy(false); }
  };
  const finish = () => run(async () => {
    onChange(await saveRitualOccurrence(ritual.id, occursOn, { note: occurrence?.note ?? null, decision }));
    finishMenu.setOpen(false);
  });
  const undo = () => run(async () => {
    await undoRitualOccurrence(ritual.id, occursOn);
    onChange(null);
  });
  const toggleHistory = () => {
    moreMenu.setOpen(false);
    setShowHistory(!showHistory);
    if (!history) void loadRitualHistory(ritual.id, occursOn).then(setHistory).catch(() => setHistory([]));
  };
  const askChat = assistant ? () => { moreMenu.setOpen(false); assistant.submitAssistantCommand(ritualChatRequest(entry)); } : null;
  const doIt = target.kind === 'app'
    ? () => navigate(launch ? launchSessionHref(launch) : appRoute(target.appKey), { state: { fromWork: true } satisfies AppObjectNavigationState })
    : target.kind === 'chat'
      ? askChat
      : () => navigate(target.kind === 'team' ? TEAM_ROUTE : target.kind === 'company' ? COMPANY_ROUTE : target.to);
  const when = occursOn === today ? describeRhythm(ritual) : `Prévu ${dayName(occursOn)}, pas encore fait`;
  const done = occurrence !== null;

  const finishCommand = (label: string) => (
    <div ref={finishMenu.ref} className="relative">
      <button type="button" aria-expanded={finishMenu.open} onClick={() => finishMenu.setOpen(!finishMenu.open)}
        className={done ? 'text-xs text-domino-inkSoft hover:text-domino-ink hover:underline' : COMMAND_CLASS}>
        {label}
      </button>
      {finishMenu.open && (
        <form className={`${MENU_CLASS} w-72 space-y-2 p-3`} onSubmit={(event) => { event.preventDefault(); void finish(); }}>
          <label className="block">
            <span className="text-xs font-semibold text-domino-ink">Ce que je décide</span>
            <Textarea autoFocus value={decision} onChange={(event) => setDecision(event.target.value)} rows={2} maxLength={4000} className="mt-1 text-sm" />
          </label>
          <Button type="submit" size="sm" loading={busy} disabled={busy}>Terminer</Button>
        </form>
      )}
    </div>
  );

  return (
    <article className={ROW_CLASS}>
      <div className="work-action-row">
        <div className="work-account min-w-0">
          <RowIdentity icon={done ? CheckCircle2 : Repeat} label="Rituel"
            detail={done ? (occurrence.decision ? `Décidé : ${occurrence.decision}` : `Fait ${occursOn === today ? 'aujourd’hui' : dayName(occursOn)}`) : when} />
        </div>
        <div className="work-menu">
          <div ref={moreMenu.ref} className="relative shrink-0">
            <button type="button" aria-label={`Options pour ${ritual.title}`} aria-expanded={moreMenu.open} onClick={() => moreMenu.setOpen(!moreMenu.open)}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {moreMenu.open && (
              <div role="menu" className={`${MENU_CLASS} w-56 py-1`}>
                {askChat && target.kind !== 'chat' && <button type="button" onClick={askChat} className={ITEM_CLASS}>Faire avec le chat</button>}
                <button type="button" onClick={toggleHistory} className={ITEM_CLASS}>Les fois précédentes</button>
              </div>
            )}
          </div>
        </div>
        <div className="work-title min-w-0">
          <h3 className={`text-sm font-normal leading-5 ${done ? 'text-domino-inkSoft' : 'text-domino-ink'}`}>{ritual.title}</h3>
          {!done && count > 0 && <p className="mt-1 text-xs text-domino-inkSoft">{count} fiche{count > 1 ? 's' : ''} à traiter</p>}
        </div>
        <div className="work-command flex min-w-0 items-center gap-2">
          {done ? (
            <span className="flex items-center gap-3">
              {finishCommand('Modifier')}
              <button type="button" onClick={() => void undo()} disabled={busy} className="text-xs text-domino-inkSoft hover:text-domino-ink hover:underline">Annuler</button>
            </span>
          ) : (
            <>
              {doIt && <button type="button" onClick={doIt} className={COMMAND_CLASS}>Faire</button>}
              {finishCommand('Terminer')}
            </>
          )}
        </div>
        {error && <p role="alert" className="col-span-full text-sm text-domino-inkSoft">{error}</p>}
        {showHistory && history && (
          <div className="col-span-full border-t border-domino-cardBorder pt-2">
            {history.length === 0
              ? <p className="text-sm text-domino-inkSoft">Première fois.</p>
              : (
                <ul className="space-y-2">
                  {history.map((past) => (
                    <li key={past.occurs_on} className="text-sm leading-6">
                      <span className="font-medium text-domino-ink">{formatDay(past.occurs_on)}</span>
                      {past.decision && <span className="block text-domino-ink">Décidé : {past.decision}</span>}
                      {past.note && <span className="block text-domino-inkSoft">{past.note}</span>}
                      {!past.decision && !past.note && <span className="block text-domino-inkSoft">Rien à signaler</span>}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        )}
      </div>
    </article>
  );
};
